import {
  gameListSchema,
  trackedPlayerListSchema,
  trackedPlayerSchema,
  type GameResponse,
  type TrackedPlayerResponse,
  type TrackPlayerRequest,
} from '@gamble-chess/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { requestJson, requestNoContent } from './client.ts';
import { queryKeys, REFRESH_INTERVAL_MS } from './queryKeys.ts';

export async function fetchTrackedPlayers(
  signal?: AbortSignal,
): Promise<readonly TrackedPlayerResponse[]> {
  const list = await requestJson(
    '/api/tracked-players',
    trackedPlayerListSchema,
    'Loading your chess accounts failed',
    { signal },
  );
  return list.players;
}

export function useTrackedPlayers(): UseQueryResult<readonly TrackedPlayerResponse[]> {
  return useQuery({
    queryKey: queryKeys.trackedPlayers,
    queryFn: ({ signal }) => fetchTrackedPlayers(signal),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export async function fetchGames(signal?: AbortSignal): Promise<readonly GameResponse[]> {
  const list = await requestJson('/api/games', gameListSchema, 'Loading your games failed', {
    signal,
  });
  return list.games;
}

export function useGames(): UseQueryResult<readonly GameResponse[]> {
  return useQuery({
    queryKey: queryKeys.games,
    queryFn: ({ signal }) => fetchGames(signal),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function postTrackedPlayer(request: TrackPlayerRequest): Promise<TrackedPlayerResponse> {
  return requestJson(
    '/api/tracked-players',
    trackedPlayerSchema,
    'Adding the account failed. Please try again.',
    { method: 'POST', body: request },
  );
}

/**
 * Tracking imports the account's recent games, which may also settle rules and wagers, so
 * those lists are refreshed too.
 */
export function useTrackPlayer(): UseMutationResult<
  TrackedPlayerResponse,
  Error,
  TrackPlayerRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postTrackedPlayer,
    onSuccess: () =>
      Promise.all(
        [queryKeys.trackedPlayers, queryKeys.games, queryKeys.wagers, queryKeys.ledger].map(
          (queryKey) => queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function deleteTrackedPlayer(id: string): Promise<void> {
  return requestNoContent(
    `/api/tracked-players/${encodeURIComponent(id)}`,
    'Removing the account failed. Please try again.',
    { method: 'DELETE' },
  );
}

export function useUntrackPlayer(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTrackedPlayer,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.trackedPlayers }),
        queryClient.invalidateQueries({ queryKey: queryKeys.games }),
      ]),
  });
}
